type KnownDeployment = {
  branch: string;
  canonicalEnvironment: string;
  convexDeployment: string;
  domain: string;
  vercelProject: string;
};

const KNOWN_DEPLOYMENTS: Record<string, KnownDeployment> = {
  dev: {
    branch: 'dev',
    canonicalEnvironment: 'development',
    convexDeployment: 'wry-spider-483',
    domain: 'dev.imai.studio',
    vercelProject: 'imai-dev',
  },
  prd: {
    branch: 'prd',
    canonicalEnvironment: 'production',
    convexDeployment: 'patient-ostrich-364',
    domain: 'imai.studio',
    vercelProject: 'imai-prd',
  },
  stg: {
    branch: 'main',
    canonicalEnvironment: 'beta',
    convexDeployment: 'quaint-octopus-429',
    domain: 'beta.imai.studio',
    vercelProject: 'imai-stg',
  },
};

const ENVIRONMENT_ALIASES: Record<string, keyof typeof KNOWN_DEPLOYMENTS> = {
  beta: 'stg',
  development: 'dev',
  prod: 'prd',
  production: 'prd',
  staging: 'stg',
};

export type PostHogDeploymentProperties = {
  convex_deployment: string;
  deployment_branch: string;
  deployment_commit: string;
  deployment_domain: string;
  deployment_environment: string;
  env: string;
  environment: string;
  vercel_project: string;
};

type ResolvePostHogDeploymentPropertiesArgs = {
  convexUrl?: string;
  hostname?: string;
  imaiEnvironment?: string;
  vercelGitCommitRef?: string;
  vercelGitCommitSha?: string;
  vercelTargetEnvironment?: string;
};

function normalize(value?: string): string {
  return value?.trim().toLowerCase() ?? '';
}

function normalizeHostname(value?: string): string {
  const normalized = normalize(value);
  if (!normalized) return '';

  try {
    return new URL(
      normalized.includes('://') ? normalized : `https://${normalized}`
    ).hostname;
  } catch {
    return normalized;
  }
}

function deploymentNameFromUrl(url?: string): string {
  if (!url) return 'unknown';

  try {
    const hostname = new URL(url).hostname;
    return (
      hostname.match(/^([a-z0-9-]+)\.convex\.(?:cloud|site)$/)?.[1] || 'unknown'
    );
  } catch {
    return 'unknown';
  }
}

function environmentFromDeployment(
  deploymentName: string
): keyof typeof KNOWN_DEPLOYMENTS | undefined {
  return Object.entries(KNOWN_DEPLOYMENTS).find(
    ([, deployment]) => deployment.convexDeployment === deploymentName
  )?.[0] as keyof typeof KNOWN_DEPLOYMENTS | undefined;
}

/** Which shared deployment a Convex URL names (`dev`/`prd`/`stg`), if any. */
export function knownDeploymentForConvexUrl(
  convexUrl?: string
): keyof typeof KNOWN_DEPLOYMENTS | undefined {
  return environmentFromDeployment(deploymentNameFromUrl(convexUrl));
}

export function resolvePostHogDeploymentProperties({
  convexUrl,
  hostname,
  imaiEnvironment,
  vercelGitCommitRef,
  vercelGitCommitSha,
  vercelTargetEnvironment,
}: ResolvePostHogDeploymentPropertiesArgs): PostHogDeploymentProperties {
  const convexDeployment = deploymentNameFromUrl(convexUrl);
  const configuredEnvironment =
    normalize(imaiEnvironment) || normalize(vercelTargetEnvironment);
  // Every Convex deployment other than the known shared ones is a personal,
  // worktree, or preview dev backend; label it instead of reporting unknown.
  const requestedEnvironment =
    configuredEnvironment ||
    environmentFromDeployment(convexDeployment) ||
    (convexDeployment === 'unknown' ? 'unknown' : 'developer');
  const knownEnvironment =
    ENVIRONMENT_ALIASES[requestedEnvironment] ?? requestedEnvironment;
  const knownDeployment = KNOWN_DEPLOYMENTS[knownEnvironment];
  const isPreview = requestedEnvironment === 'preview';

  return {
    convex_deployment: convexDeployment,
    deployment_branch:
      normalize(vercelGitCommitRef) ||
      knownDeployment?.branch ||
      (isPreview ? 'preview' : 'unknown'),
    deployment_commit: vercelGitCommitSha?.trim() || 'unknown',
    deployment_domain:
      normalizeHostname(hostname) || knownDeployment?.domain || 'unknown',
    deployment_environment:
      knownDeployment?.canonicalEnvironment ||
      (isPreview ? 'preview' : requestedEnvironment),
    env: requestedEnvironment,
    environment: requestedEnvironment,
    vercel_project:
      knownDeployment?.vercelProject || (isPreview ? 'imai-dev' : 'unknown'),
  };
}
